/**
 * Inbox → Tasks: one list of the signed-in person's own to-dos, built from
 * EXISTING endpoints only (docs/71 §9 "Tasks holds an employee's own
 * to-dos"; nav-build spec C). Plain .ts so `npm run test:unit` covers it —
 * the React hook (hooks/use-my-tasks.ts) only fetches and hands the raw
 * responses to `buildInboxTasks`.
 *
 * Every row says what to do (title), why it is here (reason), an optional
 * due date, a date-aware status pill and the owning page to open: a Me page
 * for one's own to-dos, a Manage page for HR work assigned to the person.
 */

import type { StatusTone } from "@/components/shared/status-badge";
import type { TodayPunching } from "@/types/attendance";
import { punchNeededOn } from "@/lib/punching";

// ---------------------------------------------------------------------------
// Row shape
// ---------------------------------------------------------------------------

export const INBOX_TASK_KINDS = [
  "exit",
  "onboarding",
  "survey",
  "expense",
  "asset",
  "attendance",
  "posh_course",
  "posh_policy",
  "self_review",
  "details",
] as const;

export type InboxTaskKind = (typeof INBOX_TASK_KINDS)[number];

export function isInboxTaskKind(v: unknown): v is InboxTaskKind {
  return typeof v === "string" && (INBOX_TASK_KINDS as readonly string[]).includes(v);
}

/** Filter labels (Title Case, docs/71 §11). */
export const INBOX_TASK_KIND_LABELS: Record<InboxTaskKind, string> = {
  exit: "Exit Tasks",
  onboarding: "Onboarding",
  survey: "Surveys",
  expense: "Expense Claims",
  asset: "Assets",
  attendance: "Attendance",
  posh_course: "POSH Course",
  posh_policy: "POSH Policy",
  self_review: "Self-Reviews",
  details: "Details",
};

export interface TaskStatus {
  tone: StatusTone;
  label: string;
}

export interface InboxTask {
  /** Stable React key, unique across kinds. */
  id: string;
  kind: InboxTaskKind;
  /** What to do — a Title Case fragment ("Fix Your Claim"). */
  title: string;
  /** Why it is here — one plain sentence. */
  reason: string;
  /** YYYY-MM-DD, when the task has a date it should be done by. */
  due?: string;
  status: TaskStatus;
  /** Route pattern ("/app/$tenantSlug/…") of the owning page. */
  to?: string;
  params?: Record<string, string>;
  search?: Record<string, string>;
  /** Which side owns the destination page. */
  side: "me" | "manage";
  /** Exit tasks carry inline Start / Mark Done (no other page for most people). */
  exitTaskId?: number;
  exitTaskStatus?: string;
}

// ---------------------------------------------------------------------------
// Dates
// ---------------------------------------------------------------------------

/** The YYYY-MM-DD part of a date or an RFC3339 instant ("" when absent). */
export function ymd(v: string | null | undefined): string {
  if (!v) return "";
  const m = /^(\d{4}-\d{2}-\d{2})/.exec(v);
  return m ? m[1] : "";
}

/** Whole days from `today` to `day` (both YYYY-MM-DD); negative = past. */
export function daysUntil(day: string, today: string): number {
  const a = Date.UTC(+today.slice(0, 4), +today.slice(5, 7) - 1, +today.slice(8, 10));
  const b = Date.UTC(+day.slice(0, 4), +day.slice(5, 7) - 1, +day.slice(8, 10));
  return Math.round((b - a) / 86_400_000);
}

/** A task is "due soon" this many days ahead (inclusive). */
export const DUE_SOON_DAYS = 3;

/**
 * Date-aware status (docs/71 §9 "dated statuses"): past the due date →
 * Overdue (danger); within DUE_SOON_DAYS → Due Soon (warning) unless the
 * base status already asks for action in its own words (warning tone,
 * e.g. "Sent Back"); otherwise the base status.
 */
export function datedStatus(
  base: TaskStatus,
  due: string | undefined,
  today: string,
): TaskStatus {
  if (!due) return base;
  const d = daysUntil(due, today);
  if (d < 0) return { tone: "danger", label: "Overdue" };
  if (d <= DUE_SOON_DAYS && base.tone !== "warning") {
    return { tone: "warning", label: "Due Soon" };
  }
  return base;
}

const NOT_STARTED: TaskStatus = { tone: "neutral", label: "Not Started" };
const IN_PROGRESS: TaskStatus = { tone: "info", label: "In Progress" };
/**
 * An open checklist step (onboarding, exit). The checklist page shows
 * "Pending" for it, so the Inbox uses the same word (persona QA: the
 * Inbox said "Not Started" for the same step).
 */
const PENDING_STEP: TaskStatus = { tone: "neutral", label: "Pending" };

// ---------------------------------------------------------------------------
// Copy helpers
// ---------------------------------------------------------------------------

const SMALL_WORDS = new Set([
  "a", "an", "the", "and", "but", "or", "nor", "for", "so", "yet", "as",
  "to", "of", "in", "on", "at", "by", "with", "from", "into", "onto",
  "over", "upon", "via", "per", "vs",
]);

/** "Knowledge transfer" → "Knowledge Transfer" (docs/71 §11 Title Case). */
export function titleCase(s: string): string {
  const words = s.split(" ");
  return words
    .map((w, i) => {
      if (!w) return w;
      const lower = w.toLowerCase();
      if (i > 0 && i < words.length - 1 && SMALL_WORDS.has(lower)) return lower;
      // Keep acronyms / fixed casing (POSH, F&F, ICC) as written.
      if (w === w.toUpperCase() && /[A-Z]/.test(w)) return w;
      // Each part of a hyphenated word: "pre-approval" → "Pre-Approval".
      return w
        .split("-")
        .map((p) => p.charAt(0).toUpperCase() + p.slice(1))
        .join("-");
    })
    .join(" ");
}

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const WEEKDAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

/** "26 Oct" — short day for titles. */
export function shortDay(day: string): string {
  return `${+day.slice(8, 10)} ${MONTHS[+day.slice(5, 7) - 1] ?? ""}`;
}

/** "Thu 26 Oct". */
export function weekdayShortDay(day: string): string {
  const wd = new Date(Date.UTC(+day.slice(0, 4), +day.slice(5, 7) - 1, +day.slice(8, 10))).getUTCDay();
  return `${WEEKDAYS[wd]} ${shortDay(day)}`;
}

// ---------------------------------------------------------------------------
// Source shapes (the subset of each DTO the builder reads)
// ---------------------------------------------------------------------------

export interface ExitTaskSource {
  id: number;
  cycle_id: number;
  task_type: string;
  status: string;
  leaver_name?: string;
  last_working_day?: string;
  cycle_status?: string;
}

export interface OnboardingTaskSource {
  instanceId: number;
  /** The joiner. */
  userId: number;
  employeeName?: string;
  task: {
    id: number;
    title: string;
    owner: string;
    status: string;
    due_date?: string;
    can_complete?: boolean;
  };
}

export interface SurveySource {
  id: number;
  title: string;
  closes_at?: string;
  is_anonymous: boolean;
}

export interface ExpenseSource {
  id: number;
  status: string;
  decided_at?: string;
  decision_notes?: string;
  approver_name?: string;
  merchant?: string;
  amountLabel: string;
}

export interface AttendanceDaySource {
  work_date: string;
  synthetic?: boolean;
  status?: string;
  first_in_at?: string | null;
  is_regularized?: boolean;
}

export interface PoshModuleSource {
  is_mandatory: boolean;
  is_active: boolean;
  status?: string; // not_started | in_progress | completed | expired | undefined
  refresher_due_on?: string | null;
}

export interface PoshPolicySource {
  id: number;
  version: number;
  acknowledged_at: string | null;
}

export interface ReviewOwedSource {
  assignment_id: number;
  cycle_id: number;
  cycle_name: string;
  cycle_end_date: string | null;
  relationship: string;
}

export interface AssetSource {
  assignment_id: number;
  asset_type_name: string;
  asset_tag?: string;
  acknowledged_at?: string | null;
}

/** An open details request addressed to the viewer (GET /me/detail-requests). */
export interface DetailsMineSource {
  id: number;
  due_date?: string;
  items_total: number;
  items_sent: number;
  items_verified: number;
}

/** An open request HR raised (GET /hr/detail-requests?status=open). */
export interface DetailsHrSource {
  id: number;
  employeeId: number;
  employeeName: string;
  due_date?: string;
  items_total: number;
  items_sent: number;
  items_verified: number;
  needs_hr: boolean;
  overdue: boolean;
}

export interface TaskSources {
  /** The viewer's own open detail requests. */
  detailsMine?: readonly DetailsMineSource[];
  /** Requests the viewer (HR) should check or chase. */
  detailsHr?: readonly DetailsHrSource[];
  exitTasks?: readonly ExitTaskSource[];
  onboarding?: readonly OnboardingTaskSource[];
  surveys?: readonly SurveySource[];
  expenses?: readonly ExpenseSource[];
  /** The person's own calendar days (with overlays) for [from, to]. */
  attendance?: {
    days: readonly AttendanceDaySource[];
    from: string;
    to: string;
    /** GET /attendance/today `punching` (migration 167): only days a punch
     *  was required on can be missing. Absent = as before. */
    punching?: TodayPunching;
  };
  poshModules?: readonly PoshModuleSource[];
  poshPolicy?: PoshPolicySource | null;
  reviewsOwed?: readonly ReviewOwedSource[];
  /** Equipment issued to the person (GET /me/assets). */
  assets?: readonly AssetSource[];
}

export interface BuildOptions {
  /** Today in IST, YYYY-MM-DD. */
  today: string;
  /** The viewer (to tell their own onboarding from a report's). */
  userId?: number;
  /** HR admins get the Manage page for exit work. */
  isHR?: boolean;
  /** Labels for exit-task types (sentence case; title-cased here). */
  exitTaskLabel?: (type: string) => string;
}

// ---------------------------------------------------------------------------
// Per-source mappers
// ---------------------------------------------------------------------------

function exitRows(src: readonly ExitTaskSource[], o: BuildOptions): InboxTask[] {
  return src
    .filter(
      (t) =>
        t.cycle_status !== "closed" &&
        t.cycle_status !== "cancelled" &&
        (t.status === "pending" || t.status === "in_progress"),
    )
    .map((t) => {
      const leaver = t.leaver_name || "a colleague";
      const label = titleCase(o.exitTaskLabel ? o.exitTaskLabel(t.task_type) : t.task_type);
      const due = ymd(t.last_working_day) || undefined;
      const base = t.status === "in_progress" ? IN_PROGRESS : PENDING_STEP;
      return {
        id: `exit-${t.id}`,
        kind: "exit" as const,
        title: label,
        reason: `HR asked you to help with ${leaver}'s exit.`,
        due,
        status: datedStatus(base, due, o.today),
        ...(o.isHR
          ? { to: "/app/$tenantSlug/hr/offboarding/$id", params: { id: String(t.cycle_id) } }
          : {}),
        side: "manage" as const,
        exitTaskId: t.id,
        exitTaskStatus: t.status,
      };
    });
}

function onboardingRows(src: readonly OnboardingTaskSource[], o: BuildOptions): InboxTask[] {
  // Only steps this viewer owns: the joiner their own ("employee") steps,
  // HR the "hr" / "it" steps, a manager the "manager" steps of a report.
  // (HR may tick any step, but the rest are someone else's to-do.)
  const owns = (s: OnboardingTaskSource) => {
    if (o.userId != null && s.userId === o.userId) return s.task.owner === "employee";
    return o.isHR ? s.task.owner === "hr" || s.task.owner === "it" : s.task.owner === "manager";
  };
  return src
    .filter((s) => s.task.status === "pending" && s.task.can_complete !== false && owns(s))
    .map((s) => {
      const own = o.userId != null && s.userId === o.userId;
      const due = ymd(s.task.due_date) || undefined;
      return {
        id: `onb-${s.instanceId}-${s.task.id}`,
        kind: "onboarding" as const,
        // The step's own title, as the checklist shows it.
        title: s.task.title,
        reason: own
          ? "A step in your onboarding checklist."
          : `A step in ${s.employeeName || "a new joiner"}'s onboarding that you own.`,
        due,
        status: datedStatus(PENDING_STEP, due, o.today),
        to: "/app/$tenantSlug/hr/onboarding/$id",
        params: { id: String(s.instanceId) },
        // A new joiner's step: the page shows Inbox → Tasks and leads back
        // there (a manager has no Onboarding list to return to).
        ...(own ? {} : { search: { from: "inbox" } }),
        side: own ? ("me" as const) : ("manage" as const),
      };
    });
}

function surveyRows(src: readonly SurveySource[], o: BuildOptions): InboxTask[] {
  return src.map((s) => {
    const due = ymd(s.closes_at) || undefined;
    return {
      id: `survey-${s.id}`,
      kind: "survey" as const,
      title: `Answer: ${s.title}`,
      reason: s.is_anonymous
        ? "HR asked for your views. Your answers are anonymous."
        : "HR asked for your views.",
      due,
      status: datedStatus(NOT_STARTED, due, o.today),
      to: "/app/$tenantSlug/me/surveys/$id",
      params: { id: String(s.id) },
      side: "me" as const,
    };
  });
}

/** A claim the approver sent back: still a draft, but it has a decision. */
export function isSentBack(e: Pick<ExpenseSource, "status" | "decided_at">): boolean {
  return e.status === "draft" && !!e.decided_at;
}

function expenseRows(src: readonly ExpenseSource[]): InboxTask[] {
  return src.filter(isSentBack).map((e) => {
    const who = e.approver_name || "Your approver";
    const what = e.merchant ? `${e.merchant}, ${e.amountLabel}` : e.amountLabel;
    return {
      id: `expense-${e.id}`,
      kind: "expense" as const,
      title: "Fix Your Claim",
      reason: e.decision_notes
        ? `${who} sent back your claim (${what}): “${e.decision_notes}”`
        : `${who} sent back your claim (${what}) for changes.`,
      status: { tone: "warning" as const, label: "Sent Back" },
      to: "/app/$tenantSlug/expenses/$id",
      params: { id: String(e.id) },
      side: "me" as const,
    };
  });
}

/**
 * Working days in [from, yesterday] with no punch and no leave: the gaps
 * My Time's day strip shows as "no record" (holidays, weekly offs and
 * approved leave / WFH come back as synthetic rows, so they are not gaps),
 * plus days the attendance job already marked absent and nobody corrected.
 *
 * Only for someone who punches: without a single punch in the window the
 * person (or the company) doesn't record attendance, and every day would
 * look "missing" — the same rule as the daily missing-day nudge (punching
 * tenants only). Newest first. `needed` (lib/punching punchNeededOn)
 * leaves out days no punch was required on — Punching off / optional, or
 * "Doesn't need to punch" (migration 167).
 */
export function missedAttendanceDays(
  days: readonly AttendanceDaySource[],
  from: string,
  to: string,
  today: string,
  needed: (date: string) => boolean = () => true,
): string[] {
  const punches = days.some((d) => !d.synthetic && !!d.first_in_at);
  if (!punches) return [];
  const accounted = new Set(
    days
      .filter((d) => d.synthetic || d.status !== "absent" || d.is_regularized)
      .map((d) => d.work_date),
  );
  const out: string[] = [];
  let cur = Date.UTC(+from.slice(0, 4), +from.slice(5, 7) - 1, +from.slice(8, 10));
  for (let i = 0; i < 400; i++) {
    const iso = new Date(cur).toISOString().slice(0, 10);
    // Today is still in progress — never ask about it.
    if (iso > to || iso >= today) break;
    if (!accounted.has(iso) && needed(iso)) out.push(iso);
    cur += 86_400_000;
  }
  return out.reverse();
}

/** More missed days than this collapse into one "Fix N Days" row. */
export const ATTENDANCE_ROWS_MAX = 3;

function attendanceRows(src: NonNullable<TaskSources["attendance"]>, o: BuildOptions): InboxTask[] {
  const missed = missedAttendanceDays(src.days, src.from, src.to, o.today, (d) =>
    punchNeededOn(src.punching, d, o.today),
  );
  const base = {
    kind: "attendance" as const,
    status: { tone: "warning" as const, label: "Missing" },
    to: "/app/$tenantSlug/my-time",
    side: "me" as const,
  };
  if (missed.length > ATTENDANCE_ROWS_MAX) {
    const oldest = missed[missed.length - 1];
    return [
      {
        ...base,
        id: "att-many",
        title: `Fix Attendance for ${missed.length} Days`,
        reason: `${missed.length} working days since ${shortDay(oldest)} have no punch and no leave. Apply leave or correct your punches.`,
      },
    ];
  }
  return missed.map((day) => ({
    ...base,
    id: `att-${day}`,
    title: `Fix Attendance for ${shortDay(day)}`,
    reason: `${weekdayShortDay(day)} has no punch and no leave. Apply leave or correct your punch.`,
    search: { day },
  }));
}

/** Days ahead of a refresher's lapse date when "Refresh" shows. */
export const POSH_REFRESH_WINDOW_DAYS = 30;

function poshCourseRows(src: readonly PoshModuleSource[], o: BuildOptions): InboxTask[] {
  const required = src.filter((m) => m.is_mandatory && m.is_active);
  if (required.length === 0) return [];
  const done = required.filter((m) => m.status === "completed").length;
  const expired = required.some((m) => m.status === "expired");
  const started = required.some((m) => m.status && m.status !== "not_started");
  const base = { kind: "posh_course" as const, to: "/app/$tenantSlug/posh/training", side: "me" as const };

  if (expired) {
    return [
      {
        ...base,
        id: "posh-course",
        title: "Refresh the POSH Course",
        reason: "Your POSH certificate has lapsed. Take the yearly refresher.",
        status: { tone: "warning", label: "Refresher Due" },
      },
    ];
  }
  if (done < required.length) {
    return [
      {
        ...base,
        id: "posh-course",
        title: "Finish the POSH Course",
        reason: `${done} of ${required.length} required modules done.`,
        status: started ? IN_PROGRESS : NOT_STARTED,
      },
    ];
  }
  // All done: offer the refresher early when one lapses within the window.
  const soonest = required
    .map((m) => ymd(m.refresher_due_on))
    .filter((d) => d && daysUntil(d, o.today) <= POSH_REFRESH_WINDOW_DAYS)
    .sort()[0];
  if (!soonest) return [];
  return [
    {
      ...base,
      id: "posh-course",
      title: "Refresh the POSH Course",
      reason: "Your POSH certificate lapses soon. Take the yearly refresher to renew it.",
      due: soonest,
      status: datedStatus({ tone: "warning", label: "Refresher Due" }, soonest, o.today),
    },
  ];
}

function poshPolicyRows(p: PoshPolicySource | null | undefined): InboxTask[] {
  if (!p || p.acknowledged_at) return [];
  return [
    {
      id: `posh-policy-${p.id}`,
      kind: "posh_policy",
      title: "Acknowledge the POSH Policy",
      reason: `Read the company's POSH policy (version ${p.version}) and confirm you understood it.`,
      status: NOT_STARTED,
      to: "/app/$tenantSlug/posh/about",
      side: "me",
    },
  ];
}

/**
 * Equipment HR issued that the person has not yet confirmed receiving
 * (Me → Assets "Confirm receipt"; the confirmation is what makes the
 * register count at recovery time, docs/60 A1).
 */
function assetRows(src: readonly AssetSource[]): InboxTask[] {
  return src
    .filter((a) => !a.acknowledged_at)
    .map((a) => ({
      id: `asset-${a.assignment_id}`,
      kind: "asset" as const,
      title: `Confirm You Received: ${a.asset_type_name}`,
      reason: `HR issued you ${a.asset_type_name}${a.asset_tag ? ` (${a.asset_tag})` : ""}. Confirm you received it.`,
      status: NOT_STARTED,
      to: "/app/$tenantSlug/me/assets",
      side: "me" as const,
    }));
}

/** Ask for Details, employee side: one row per open request, same data as the Me home card. */
function detailsMineRows(src: readonly DetailsMineSource[], o: BuildOptions): InboxTask[] {
  return src.map((r) => {
    const due = ymd(r.due_date) || undefined;
    const sent = Math.max(r.items_sent, r.items_verified);
    return {
      id: `details-${r.id}`,
      kind: "details" as const,
      title: `Finish Your Details · ${sent} of ${r.items_total} sent`,
      reason: "HR asked you for a few details or documents.",
      due,
      status: datedStatus(NOT_STARTED, due, o.today),
      to: "/app/$tenantSlug/me/details",
      side: "me" as const,
    };
  });
}

/**
 * Ask for Details, HR side: "Check X's Details (n)" when items wait for a
 * check; "X's Details Overdue" when past due with items not sent.
 */
function detailsHrRows(src: readonly DetailsHrSource[], o: BuildOptions): InboxTask[] {
  const rows: InboxTask[] = [];
  for (const r of src) {
    const due = ymd(r.due_date) || undefined;
    const toCheck = Math.max(0, r.items_sent - r.items_verified);
    const base = {
      kind: "details" as const,
      due,
      to: "/app/$tenantSlug/people/$id",
      params: { id: String(r.employeeId) },
      side: "manage" as const,
    };
    if (r.needs_hr && toCheck > 0) {
      rows.push({
        ...base,
        id: `details-check-${r.id}`,
        title: `Check ${r.employeeName}'s Details (${toCheck})`,
        reason: `${r.employeeName} sent details or documents for you to check.`,
        status: { tone: "warning", label: "To Check" },
      });
    } else if (r.overdue && r.items_sent < r.items_total) {
      rows.push({
        ...base,
        id: `details-overdue-${r.id}`,
        title: `${r.employeeName}'s Details Overdue`,
        reason: `${r.items_total - r.items_sent} of ${r.items_total} items have not been sent yet.`,
        status: datedStatus(NOT_STARTED, due, o.today),
      });
    }
  }
  return rows;
}

function selfReviewRows(src: readonly ReviewOwedSource[], o: BuildOptions): InboxTask[] {
  return src
    .filter((r) => r.relationship === "self")
    .map((r) => {
      const due = ymd(r.cycle_end_date) || undefined;
      return {
        id: `review-${r.assignment_id}`,
        kind: "self_review" as const,
        title: "Write Your Self-Review",
        reason: `Part of the ${r.cycle_name} review cycle.`,
        due,
        status: datedStatus(NOT_STARTED, due, o.today),
        to: "/app/$tenantSlug/performance/reviews/$id",
        params: { id: String(r.cycle_id) },
        side: "me" as const,
      };
    });
}

// ---------------------------------------------------------------------------
// Builder
// ---------------------------------------------------------------------------

const TONE_RANK: Record<StatusTone, number> = {
  danger: 0,
  warning: 1,
  progress: 2,
  info: 2,
  neutral: 3,
  success: 4,
};

/**
 * Every task across the sources, most urgent first: overdue, then other
 * action-needed rows, then by due date (undated last), then kind order.
 */
export function buildInboxTasks(s: TaskSources, o: BuildOptions): InboxTask[] {
  const rows: InboxTask[] = [
    ...exitRows(s.exitTasks ?? [], o),
    ...onboardingRows(s.onboarding ?? [], o),
    ...expenseRows(s.expenses ?? []),
    ...assetRows(s.assets ?? []),
    ...(s.attendance ? attendanceRows(s.attendance, o) : []),
    ...poshCourseRows(s.poshModules ?? [], o),
    ...poshPolicyRows(s.poshPolicy),
    ...selfReviewRows(s.reviewsOwed ?? [], o),
    ...surveyRows(s.surveys ?? [], o),
    ...detailsMineRows(s.detailsMine ?? [], o),
    ...detailsHrRows(s.detailsHr ?? [], o),
  ];
  const kindRank = (k: InboxTaskKind) => INBOX_TASK_KINDS.indexOf(k);
  return rows
    .map((r, i) => ({ r, i }))
    .sort((a, b) => {
      const t = TONE_RANK[a.r.status.tone] - TONE_RANK[b.r.status.tone];
      if (t !== 0) return t;
      const ad = a.r.due ?? "9999-12-31";
      const bd = b.r.due ?? "9999-12-31";
      if (ad !== bd) return ad < bd ? -1 : 1;
      const k = kindRank(a.r.kind) - kindRank(b.r.kind);
      return k !== 0 ? k : a.i - b.i;
    })
    .map(({ r }) => r);
}

/** The kinds present in a task list, in filter order. */
export function presentKinds(tasks: readonly InboxTask[]): InboxTaskKind[] {
  const have = new Set(tasks.map((t) => t.kind));
  return INBOX_TASK_KINDS.filter((k) => have.has(k));
}
